import api from './api';

/**
 * First-Party-Analytics: schickt SPA-Pageviews ans EIGENE Backend
 * (POST /api/analytics/track) — zusätzlich zu GA, aber unabhängig vom
 * Cookie-Consent. Das ist DSGVO-konform möglich, weil:
 *
 * - KEINE persistente Client-ID: die Session-ID lebt nur im
 *   sessionStorage (Tab-Lebensdauer) und identifiziert niemanden
 *   über die Sitzung hinaus.
 * - Unique-Besucher bestimmt der SERVER per Tages-Hash — der Client
 *   liefert dafür keinerlei dauerhaften Identifier.
 * - Fail-soft: Tracking-Fehler dürfen NIE die App stören (nur
 *   console.debug, niemals werfen).
 *
 * Admin-Pfade (/admin/...) werden nicht getrackt — Eigenbesuche würden
 * die Statistik verzerren.
 */

export const SESSION_STORAGE_KEY = 'dc-analytics-session';

/** UUID v4 — crypto.randomUUID mit Math.random-Fallback (alte Browser/Jest) */
const generateId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* weiter zum Fallback */
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Session-ID: UUID im sessionStorage — stabil innerhalb der Browser-Session,
 * weg beim Schließen des Tabs. Bewusst KEIN localStorage (wäre ein
 * persistenter Identifier und damit consent-pflichtig).
 */
export const getSessionId = (): string => {
  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const id = generateId();
    sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    return id;
  } catch {
    // sessionStorage gesperrt → einmalige ID pro Aufruf (Tracking läuft trotzdem)
    return generateId();
  }
};

/**
 * page_view ans eigene Backend melden. userId ist optional und kommt vom
 * Aufrufer (AnalyticsTracker via useAuth) — der Service selbst liest
 * bewusst keine Auth-Daten.
 */
export const trackPageView = async (
  path: string,
  title?: string,
  userId?: string | null
): Promise<void> => {
  try {
    if (!path || path === '/admin' || path.startsWith('/admin/')) return;
    await api.post('/api/analytics/track', {
      eventName: 'page_view',
      sessionId: getSessionId(),
      userId: userId ?? null,
      deviceInfo: {
        platform: 'web',
        userAgent: navigator.userAgent,
        screenWidth: window.screen?.width ?? null,
        screenHeight: window.screen?.height ?? null,
        language: navigator.language,
      },
      url: window.location.href,
      referrer: document.referrer || null,
      parameters: {
        page_path: path,
        page_title: title ?? document.title,
      },
    });
  } catch (err) {
    // Fail-soft: Tracking darf die App NIE stören
    console.debug('firstPartyAnalytics: page_view nicht gesendet', err);
  }
};

const firstPartyAnalytics = { getSessionId, trackPageView };
export default firstPartyAnalytics;
